import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import ModuleDisabledState from "../../../components/dashboard/ModuleDisabledState";
import { projectsApi } from "../../../api/projects";
import { erpApi } from "../../../api/erp";
import { useProjects } from "../../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../../utils/projectModules";
import { ERP_BRAND } from "../../../utils/erp";

const TABS = [
  { key: "overview", label: "Resumen", path: "", icon: <SpaceDashboardRoundedIcon /> },
  { key: "movements", label: "Movimientos", path: "movimientos", icon: <ReceiptLongRoundedIcon /> },
  { key: "cash", label: "Caja", path: "caja", icon: <PointOfSaleRoundedIcon /> },
  { key: "ledger", label: "Libro diario", path: "ledger", icon: <AccountBalanceRoundedIcon /> },
  { key: "transactions", label: "Transacciones", path: "transacciones", icon: <SwapHorizRoundedIcon /> },
  { key: "stock", label: "Stock", path: "stock", icon: <InventoryRoundedIcon /> },
  { key: "accounts", label: "Cuentas", path: "cuentas", icon: <AccountTreeRoundedIcon /> },
];

function tabValue(pathname) {
  const match = TABS.find((tab) => tab.path && pathname.endsWith(`/${tab.path}`));
  return match?.key || "overview";
}

export default function ProjectErpLayout() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [bootstrap, setBootstrap] = React.useState({ accounts: [], registers: [], openSessions: [] });
  const [loading, setLoading] = React.useState(true);
  const [bootstrapping, setBootstrapping] = React.useState(false);
  const [error, setError] = React.useState("");

  const contextProject = React.useMemo(
    () => projects.find((item) => item.slug === projectId || item.id === projectId) || activeProject,
    [projects, projectId, activeProject],
  );
  const resolvedProject = project || contextProject;
  const enabled = isProjectModuleEnabled(resolvedProject, "system");

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!mounted) return;
        setProject(data.project || null);
        if (data.project) setActiveProject(data.project);
      })
      .catch((err) => {
        if (mounted) setError(err.message || "No se pudo cargar el proyecto");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [projectId, setActiveProject]);

  const refreshBootstrap = React.useCallback(async () => {
    if (!projectId || !enabled) return null;
    try {
      setBootstrapping(true);
      const data = await erpApi.bootstrap(projectId);
      const next = {
        accounts: data.accounts || [],
        registers: data.registers || [],
        openSessions: data.openSessions || [],
      };
      setBootstrap(next);
      return next;
    } catch (err) {
      setError(err.message || "No se pudo inicializar el ERP");
      return null;
    } finally {
      setBootstrapping(false);
    }
  }, [projectId, enabled]);

  React.useEffect(() => {
    if (!loading && enabled) refreshBootstrap();
  }, [loading, enabled, refreshBootstrap]);

  if (loading) return <Alert severity="info">Cargando sistema...</Alert>;

  if (!enabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Sistema ERP"
        description="Este módulo incorpora movimientos, caja, stock, transacciones y contabilidad operativa para el proyecto."
      />
    );
  }

  const base = `/dashboard/proyectos/${projectId}/sistema`;

  return (
    <Stack spacing={2.5}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}

      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap" useFlexGap>
            <Typography variant="h3" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
              Sistema ERP
            </Typography>
            <Chip label="Multicliente" color="success" size="small" sx={{ fontWeight: 850 }} />
          </Stack>
          <Typography sx={{ color: ERP_BRAND.muted, mt: 0.5 }}>
            {resolvedProject?.name || projectId} · movimientos, caja, stock y libro diario
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshRoundedIcon />}
          disabled={bootstrapping}
          onClick={refreshBootstrap}
          sx={{ alignSelf: "flex-start", borderRadius: 2.5, textTransform: "none", fontWeight: 850 }}
        >
          Actualizar estructura
        </Button>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${ERP_BRAND.border}`,
          overflowX: "auto",
        }}
      >
        <Tabs
          value={tabValue(location.pathname)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 1,
            minHeight: 58,
            "& .MuiTab-root": { minHeight: 58, textTransform: "none", fontWeight: 850 },
          }}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              onClick={() => navigate(tab.path ? `${base}/${tab.path}` : base)}
            />
          ))}
        </Tabs>
      </Paper>

      <Outlet
        context={{
          project: resolvedProject,
          bootstrap,
          refreshBootstrap,
          setGlobalError: setError,
        }}
      />
    </Stack>
  );
}
